(function () {
  'use strict';

  var section = document.querySelector('[data-quiz-type]');
  if (!section) return;

  var quizType = section.dataset.quizType;
  var configUrl = section.dataset.configUrl;

  var questionsEl = document.getElementById('hiba-quiz-questions');
  var gateEl = document.getElementById('hiba-quiz-gate');
  var progressBar = document.querySelector('.hiba-quiz__progress-bar');

  if (!questionsEl) return;

  var questions = [];
  var tiers = [];
  var answers = [];
  var stepFiftyFired = false;

  // ── Meta Pixel helpers ─────────────────────────────────────────
  // Per-event eventID enables CAPI dedup (browser fbq + server-side both
  // include the same ID; Meta collapses to one event within 48h window).
  function makeEventId(eventName) {
    var rand = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : (Date.now() + '_' + Math.random().toString(36).slice(2, 10));
    return 'hiba_' + eventName.toLowerCase() + '_' + rand;
  }

  function firePixelEvent(eventName, props) {
    if (typeof window.fbq !== 'function') return;
    try {
      window.fbq('trackCustom', eventName, props || {}, { eventID: makeEventId(eventName) });
    } catch (err) {
      console.warn('[hiba-quiz] fbq ' + eventName + ' error', err);
    }
  }

  // ── Load JSON config (10s timeout via AbortController) ───────
  var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
  var timeoutId = controller ? setTimeout(function () { controller.abort(); }, 10000) : null;

  fetch(configUrl, controller ? { signal: controller.signal } : {})
    .then(function (r) { return r.json(); })
    .then(function (data) {
      if (timeoutId) clearTimeout(timeoutId);
      questions = data.questions;
      tiers = data.tiers || [];
      // Fire QuizStarted once config loads — gives Meta a high-volume upstream
      // signal vs the deep email-gate Lead. Proxy-event optimization target.
      firePixelEvent('QuizStarted', {
        quiz_type: klaviyoLens(quizType),
        content_category: klaviyoLens(quizType),
        content_name: 'quiz-started-' + klaviyoLens(quizType),
        question_count: questions.length
      });
      renderQuestion(0);
    })
    .catch(function (err) {
      if (timeoutId) clearTimeout(timeoutId);
      while (questionsEl.firstChild) questionsEl.removeChild(questionsEl.firstChild);
      var p = document.createElement('p');
      p.style.cssText = 'color:var(--fg-muted);font-family:var(--serif);font-style:italic;padding:2rem 0';
      p.textContent = 'Unable to load quiz. Please refresh the page.';
      questionsEl.appendChild(p);
      console.error('hiba-quiz: failed to load config', err);
      // Track load failure to Klaviyo so we can measure rate.
      try {
        if (window._learnq && Array.isArray(window._learnq)) {
          window._learnq.push(['track', 'Quiz Load Failed', { quiz_type: quizType }]);
        }
      } catch (_) {}
    });

  // ── Progress bar ──────────────────────────────────────────────
  // Updates two surfaces: the (subtle) sticky top bar and the
  // in-question hairline rule (driven by --quiz-progress on the
  // section element, read by .hiba-quiz__q-text::after).
  function updateProgress(idx) {
    if (!questions.length) return;
    var pct = (idx / questions.length) * 100;
    if (progressBar) progressBar.style.width = pct + '%';
    var inlinePct = Math.min(100, ((idx + 1) / questions.length) * 100);
    if (section) section.style.setProperty('--quiz-progress', inlinePct + '%');
  }

  // ── Render one question (safe DOM methods) ───────────────────
  function renderQuestion(idx) {
    var q = questions[idx];
    if (!q) return;

    updateProgress(idx);

    var oldEl = questionsEl.querySelector('.hiba-quiz__question.is-active');

    var div = document.createElement('div');
    div.className = 'hiba-quiz__question';
    div.setAttribute('role', 'group');
    div.setAttribute('aria-label', 'Question ' + (idx + 1) + ' of ' + questions.length);

    var count = document.createElement('span');
    count.className = 'hiba-quiz__q-count';
    count.textContent = (idx + 1) + ' / ' + questions.length;
    div.appendChild(count);

    var qText = document.createElement('p');
    qText.className = 'hiba-quiz__q-text';
    qText.textContent = q.text;
    div.appendChild(qText);

    var ul = document.createElement('ul');
    ul.className = 'hiba-quiz__options';
    ul.setAttribute('role', 'list');

    q.options.forEach(function (opt, oIdx) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.className = 'hiba-quiz__option';
      btn.type = 'button';
      btn.dataset.optionIdx = String(oIdx);
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');

      var label = document.createElement('span');
      label.className = 'hiba-quiz__option-label';
      label.textContent = opt.label;
      btn.appendChild(label);

      li.appendChild(btn);
      ul.appendChild(li);

      btn.addEventListener('click', function () {
        recordAnswer(idx, oIdx, div);
      });
    });

    div.appendChild(ul);

    if (oldEl) {
      oldEl.classList.remove('is-active');
      oldEl.classList.add('is-exiting');
      setTimeout(function () { if (oldEl.parentNode) oldEl.parentNode.removeChild(oldEl); }, 550);
    }

    questionsEl.appendChild(div);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        div.classList.add('is-active');
      });
    });
  }

  // ── Record answer ─────────────────────────────────────────────
  function recordAnswer(qIdx, oIdx, questionEl) {
    var q = questions[qIdx];
    var opt = q.options[oIdx];

    questionEl.querySelectorAll('.hiba-quiz__option').forEach(function (b) {
      b.classList.remove('is-selected');
      b.setAttribute('aria-checked', 'false');
      b.disabled = true;
    });
    var sel = questionEl.querySelectorAll('.hiba-quiz__option')[oIdx];
    sel.classList.add('is-selected');
    sel.setAttribute('aria-checked', 'true');

    answers[qIdx] = opt;

    // Fire per-question event for funnel-step analysis in Events Manager.
    // Breakdown by question_index reveals exact drop-off per question.
    // Audience building unlock: "users who got to Q14 but didn't complete"
    // becomes a Custom Audience — highest-intent retargeting pool.
    firePixelEvent('QuizQuestionAnswered', {
      quiz_type: klaviyoLens(quizType),
      content_category: klaviyoLens(quizType),
      content_name: 'quiz-q' + (qIdx + 1) + '-' + klaviyoLens(quizType),
      question_index: qIdx + 1,
      question_count: questions.length,
      question_text_short: (q.text || '').substring(0, 60)
    });

    // Fire QuizStep_50 once when user crosses the midpoint. Useful as a
    // diagnostic for where drop-off happens AND as a richer-than-LP-View
    // signal for retargeting drop-offs.
    if (!stepFiftyFired && (qIdx + 1) >= Math.floor(questions.length / 2)) {
      stepFiftyFired = true;
      firePixelEvent('QuizStep_50', {
        quiz_type: klaviyoLens(quizType),
        content_category: klaviyoLens(quizType),
        content_name: 'quiz-step-50-' + klaviyoLens(quizType),
        step: qIdx + 1,
        question_count: questions.length
      });
    }

    // Grow the hairline rule toward the next question's percentage
    // during the held state — telegraphs forward motion before the
    // page turn.
    if (section) {
      var nextPct = Math.min(100, ((qIdx + 2) / questions.length) * 100);
      section.style.setProperty('--quiz-progress', nextPct + '%');
    }

    setTimeout(function () {
      if (qIdx < questions.length - 1) {
        renderQuestion(qIdx + 1);
      } else {
        updateProgress(questions.length);
        completeQuiz();
      }
    }, 600);
  }

  // ────────────────────────────────────────────────────────────────
  // SCORING — two systems coexist:
  //   - Identity (persona) quiz: ARCHETYPE scoring (count type tags → winner)
  //   - Money / Love quizzes:    SEVERITY scoring (sum scores → tier)
  // Per Hiba's clinical design (verified in her quiz docs 2026-04-30).
  // ────────────────────────────────────────────────────────────────

  // For Identity: count archetype tags
  function calculateArchetype() {
    var counts = { good_girl: 0, peacekeeper: 0, performer: 0 };
    answers.forEach(function (a) {
      if (a && a.type && counts.hasOwnProperty(a.type)) counts[a.type] += 1;
    });
    var sorted = Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; });
    var winner = sorted[0];
    // Tie-break: use Q13 (idx 12) then Q1 (idx 0)
    if (counts[sorted[0]] === counts[sorted[1]]) {
      winner = (answers[12] && answers[12].type) || (answers[0] && answers[0].type) || 'good_girl';
    }
    return winner;
  }

  // For Money / Love: sum scores → percentage + band
  // v3 architecture (2026-04-30): score becomes a display element, not a routing key.
  // Single result page per quiz; band drives which copy block renders.
  function calculateSeverity() {
    var total = 0;
    answers.forEach(function (a) {
      if (a && typeof a.score === 'number') total += a.score;
    });
    // Max score is 45 (15 questions × 3pt max). Round to whole percent.
    var percentage = Math.round((total / 45) * 100);
    // Map raw score to Hiba's 4 bands per her quiz docs.
    var band;
    if (total <= 9) band = 'quiet';        //  0–20%   "Low Pattern Activation"
    else if (total <= 21) band = 'present'; // 22–47%  "Pattern Present, Lower Activation"
    else if (total <= 34) band = 'active';  // 49–76%  "Pattern Active, Partially Visible"
    else band = 'deep';                     // 78–100% "Deep Pattern Running"
    return { score: total, percentage: percentage, band: band };
  }

  // ── Mappings ──────────────────────────────────────────────────
  function klaviyoLens(qt) {
    if (qt === 'money') return 'money';
    if (qt === 'love') return 'love';
    return 'identity';
  }

  function urlLens(qt) {
    if (qt === 'money') return 'money';
    if (qt === 'love') return 'love';
    return 'obedience';
  }

  // ── Push to Klaviyo (anonymous profile) ───────────────────────
  // Profile gets tagged with quiz result properties BEFORE redirect. When the
  // popup on the result page captures email, Klaviyo merges email with this
  // already-tagged anonymous profile → her welcome flow triggers.
  // Returns true on successful push, false if Klaviyo is unavailable/blocked.
  function pushToKlaviyo(result) {
    try {
      // Guard: _learnq may be undefined (Klaviyo SDK not loaded), or
      // truthy-but-not-array if a third-party script clobbered it.
      if (!window._learnq || !Array.isArray(window._learnq)) {
        console.error('hiba-quiz: Klaviyo SDK unavailable — lead may be lost', { quizType: quizType });
        return false;
      }
      var props = {};
      var klavLens = klaviyoLens(quizType);
      props['quiz_completed_' + klavLens] = true;

      if (quizType === 'persona') {
        // Identity quiz: archetype-based properties
        props['archetype_' + result.archetype] = true;
        props.quiz_archetype = result.archetype;
      } else {
        // Money / Love (v3): percentage + band properties
        // Number() casts defend against future refactors that might stringify these.
        props['quiz_' + klavLens + '_score'] = Number(result.score);            // raw 0–45
        props['quiz_' + klavLens + '_percentage'] = Number(result.percentage);  // 0–100
        props['quiz_' + klavLens + '_band'] = String(result.band);              // quiet/present/active/deep
        // Backward-compat alias: keep _tier in sync with _band for one release
        // so any legacy Klaviyo flows segmenting on _tier don't silently break.
        props['quiz_' + klavLens + '_tier'] = String(result.band);
      }

      window._learnq.push(['identify', props]);

      var trackProps = { quiz_type: klavLens };
      if (quizType === 'persona') {
        trackProps.archetype = result.archetype;
      } else {
        trackProps.score = Number(result.score);
        trackProps.percentage = Number(result.percentage);
        trackProps.band = String(result.band);
      }
      window._learnq.push(['track', 'Quiz Completed', trackProps]);
      return true;
    } catch (err) {
      console.error('hiba-quiz: Klaviyo push failed — lead may be lost', err);
      return false;
    }
  }

  // ── Hide questions (transition out) ───────────────────────────
  function hideQuestions() {
    questionsEl.style.transition = 'opacity 200ms ease-out';
    questionsEl.style.opacity = '0';
    setTimeout(function () {
      questionsEl.setAttribute('hidden', '');
    }, 200);
  }

  // ── Reveal in-quiz email step + wait for Klaviyo submit ───────
  function showEmailStepAndAwaitSubmit(redirectUrl) {
    var emailStepEl = document.getElementById('hiba-quiz-email-step');
    if (!emailStepEl) {
      // No email-step in DOM (legacy template) — fail-safe direct redirect.
      setTimeout(function () { window.location.assign(redirectUrl); }, 350);
      return;
    }

    var emailFormId = emailStepEl.dataset.klaviyoFormId || '';
    // Add active class — CSS uses it to hide progress bar + collapse
    // questions container so the email step doesn't sit in a void.
    if (section) section.classList.add('hiba-quiz--email-step-active');
    hideQuestions();
    setTimeout(function () {
      emailStepEl.removeAttribute('hidden');
    }, 220);

    var submitted = false;
    window.addEventListener('klaviyoForms', function (e) {
      if (submitted || !e.detail) return;
      var eventFormId = e.detail.formId;
      if (emailFormId && eventFormId && eventFormId !== emailFormId) return;
      if (e.detail.type === 'submit') {
        submitted = true;
        // Let Klaviyo flush its capture event before navigation.
        setTimeout(function () {
          window.location.assign(redirectUrl);
        }, 600);
      }
    });
  }

  // ── Direct loading state (persona-only fallback path) ─────────
  function showLoadingThenRedirect(redirectUrl) {
    hideQuestions();
    var legacyGate = document.getElementById('hiba-quiz-gate');
    if (legacyGate) {
      setTimeout(function () { legacyGate.removeAttribute('hidden'); }, 220);
    }
    setTimeout(function () {
      window.location.assign(redirectUrl);
    }, 350);
  }

  // ── Quiz complete → push tags + reveal email step / redirect ──
  function completeQuiz() {
    var urlLensSlug = urlLens(quizType);
    var resultData;
    var redirectUrl;

    if (quizType === 'persona') {
      var archetype = calculateArchetype();
      var archetypeSlug = archetype.replace(/_/g, '-');
      resultData = { archetype: archetype };
      redirectUrl = '/pages/' + urlLensSlug + '-' + archetypeSlug;
    } else {
      var sev = calculateSeverity();
      resultData = { score: sev.score, percentage: sev.percentage, band: sev.band };
      redirectUrl = '/pages/' + urlLensSlug + '-result?score=' + sev.percentage;
    }

    pushToKlaviyo(resultData);

    // Fire QuizCompleted — primary proxy-event optimization target.
    var completedProps = {
      quiz_type: klaviyoLens(quizType),
      content_category: klaviyoLens(quizType),
      content_name: 'quiz-completed-' + klaviyoLens(quizType)
    };
    if (quizType === 'persona') {
      completedProps.archetype = resultData.archetype;
    } else {
      completedProps.score = resultData.score;
      completedProps.percentage = resultData.percentage;
      completedProps.band = resultData.band;
    }
    firePixelEvent('QuizCompleted', completedProps);

    // All quizzes (Money / Love / Persona) now use the in-quiz email step.
    // Persona redirects to /pages/obedience-{archetype} after capture; the
    // OLD "Hiba Obedience Form" (Full Page) firing on those archetype URLs
    // must be UNTARGETED in Klaviyo separately (see Tian's asks list).
    showEmailStepAndAwaitSubmit(redirectUrl);
  }

})();
